import { OVERLAY_PRIO, useOverlaySlot } from '../lib/overlays';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, ChevronRight, Clock, Crown, WifiOff, Star, Phone, Spade, Trophy, Gamepad2, Check } from 'lucide-react';
import { SHOP_ITEMS_MAP } from '../lib/shopItems';
import './Home.css';
import * as api from '../lib/api';
import type { User, Tournament, UserRating, RatingPosition, RegistrationStatus, PlayerRatingRow } from '../lib/types';
import { getTournamentBg } from '../lib/types';
import { getCurrentSeries } from '../lib/ratingSeries';
import { SkeletonTournamentCard, SkeletonRatingBanner, SkeletonPodium } from '../components/Skeleton';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import { getLoyaltyStatus, type LoyaltyStatus } from '../lib/loyalty';
import { LoyaltyCelebration, LoyaltyHomeCard } from '../components/Loyalty';
import { fmtNum } from '../lib/format';

type PodiumTab = 'overall' | 'series';

const CLUB_ADDRESS =
  import.meta.env.VITE_CLUB_ADDRESS || 'Москва, проспект Мира, дом 26, строение 2';

interface HomeProps {
  user: User;
}

export default function Home({ user }: HomeProps) {
  const navigate = useNavigate();
  const [upcomingTournament, setUpcomingTournament] = useState<Tournament | null>(null);
  const [registrationStatus, setRegistrationStatus] = useState<RegistrationStatus | null>(null);
  const [userRating, setUserRating] = useState<UserRating | null>(null);
  const [userPosition, setUserPosition] = useState<RatingPosition | null>(null);
  const [userSeriesPosition, setUserSeriesPosition] = useState<RatingPosition | null>(null);
  const [topPlayers, setTopPlayers] = useState<PlayerRatingRow[]>([]);
  const [seriesTopPlayers, setSeriesTopPlayers] = useState<PlayerRatingRow[]>([]);
  const [podiumTab, setPodiumTab] = useState<PodiumTab>('overall');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loyalty, setLoyalty] = useState<LoyaltyStatus | null>(null);
  const [celebrate, setCelebrate] = useState(false);
  const celebrationVisible = useOverlaySlot('loyalty-celebration', celebrate, OVERLAY_PRIO.loyaltyCelebration, true);

  // Weekly streak loads on its own so it never delays the rest of the page.
  useEffect(() => {
    let cancelled = false;
    getLoyaltyStatus(user.id)
      .then(s => { if (cancelled) return; setLoyalty(s); if (s.unseen.length) setCelebrate(true); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user.id, refreshKey]);

  const currentSeries = getCurrentSeries();

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadError(false);

      let failed = 0;
      const [rating, position, seriesPosition, top3, seriesTop3, tournaments] = await Promise.all([
        api.getUserRating(user.id).catch(() => { failed++; return null; }),
        api.getUserRatingPosition(user.id, 'overall').catch(() => { failed++; return null; }),
        // Series position throws if the user has no results in the
        // active series yet — that's expected, not a network failure,
        // so it doesn't contribute to the offline-detection counter.
        api.getUserSeriesPosition(user.id, currentSeries.startDate, currentSeries.endDateExclusive).catch(() => null),
        api.getTopRatings(3).catch(() => { failed++; return []; }),
        api.getSeriesRatings(currentSeries.startDate, currentSeries.endDateExclusive, 3).catch(() => []),
        api.getTournaments(20).catch(() => { failed++; return []; }),
      ]);

      if (failed === 4) {
        setLoadError(true);
        setLoading(false);
        return;
      }

      if (cancelled) return;

      setUserRating(rating);
      setUserPosition(position);
      setUserSeriesPosition(seriesPosition);
      setTopPlayers(top3);
      setSeriesTopPlayers(seriesTop3 as unknown as PlayerRatingRow[]);

      // Show on the home screen any tournament that hasn't been marked
      // finished — including ones that have already started. The card stays
      // until an admin presses "Завершить турнир" (sets is_finished=true).
      const upcoming = Array.isArray(tournaments)
        ? tournaments
          .filter((t: any) => !t.is_finished)
            .sort(
              (a: any, b: any) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
            )[0]
        : null;

      if (!upcoming?.id) {
        setUpcomingTournament(null);
        setRegistrationStatus(null);
        setLoading(false);
        return;
      }

      // Show page immediately, load tournament details in background
      setLoading(false);

      const [full, status] = await Promise.all([
        api.getTournamentById(upcoming.id).catch(() => null),
        api.getRegistrationStatus(upcoming.id, user.id).catch(() => null),
      ]);
      if (cancelled) return;
      setUpcomingTournament(full);
      setRegistrationStatus(status);
    };

    load().catch((e) => {
      console.error(e);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [user.id, refreshKey]);

  usePullToRefresh(async () => {
    setRefreshKey(k => k + 1);
  });

  const displayName = useMemo(() => {
    if (user?.display_name) return user.display_name;
    if (user?.username) return user.username;
    return [user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'Игрок';
  }, [user?.display_name, user?.username, user?.first_name, user?.last_name]);

  const currentRating = userRating?.current_rating ?? 0;

  const MOSCOW_TZ = 'Europe/Moscow';

  const handleOpenMaps = () => {
    const url =
      import.meta.env.VITE_CLUB_MAP_URL ||
      `https://yandex.ru/maps/?text=${encodeURIComponent(CLUB_ADDRESS)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="home-dash">
        <h1 className="home-greeting">Привет, {displayName}!</h1>
        <h2 className="home-section-title">Ближайший турнир</h2>
        <SkeletonTournamentCard />
        <SkeletonRatingBanner />
        <SkeletonPodium />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="home-dash" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 16 }}>
        <WifiOff size={44} strokeWidth={1.6} color="rgba(255,255,255,0.55)" />
        <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>Нет соединения</div>
        <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>Не удалось загрузить данные.<br/>Проверьте интернет и попробуйте снова.</div>
        <button
          onClick={() => setRefreshKey(k => k + 1)}
          style={{ marginTop: 8, padding: '12px 28px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg,#f2c14b,#d79a2b)', color: '#000', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
        >
          Обновить
        </button>
      </div>
    );
  }

  const formatDay = (dateString: string) => {
    const d = new Date(dateString);
    const wd = d.toLocaleDateString('ru-RU', { timeZone: MOSCOW_TZ, weekday: 'short' });
    const dm = d.toLocaleDateString('ru-RU', { timeZone: MOSCOW_TZ, day: 'numeric', month: 'long' });
    return `${wd.charAt(0).toUpperCase()}${wd.slice(1)}, ${dm}`;
  };
  const formatTime = (dateString: string) =>
    new Date(dateString).toLocaleTimeString('ru-RU', { timeZone: MOSCOW_TZ, hour: '2-digit', minute: '2-digit' });

  return (
    <div className="home-dash">

      {/* 0) Приветствие */}
      <h1 className="home-greeting">Привет, {displayName}!</h1>

      {/* 1) Ближайший турнир - заголовок */}
      <div className="home-section-header">
        <h2 className="home-section-title">Ближайший турнир</h2>
      </div>

      {/* 2) Карточка турнира */}
      {upcomingTournament ? (
        <div
          className="home-tcard"
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/tournament/${upcomingTournament.id}`)}
          style={{
            backgroundImage: `linear-gradient(105deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0.2) 100%), url('${getTournamentBg(upcomingTournament.tournament_type).url}')`,
            backgroundPosition: getTournamentBg(upcomingTournament.tournament_type).position,
          }}
        >
          <div className="home-tcard__body">
            {(() => {
              const t = upcomingTournament;
              const isReg = registrationStatus?.status === 'registered';
              const isWait = registrationStatus?.status === 'waitlist';
              const spotsLeft = Math.max(0, (t.max_players || 0) - (t.current_players || 0));
              const full = t.is_sold_out || spotsLeft === 0;
              const fill = t.max_players ? Math.min(100, ((t.current_players || 0) / t.max_players) * 100) : 0;
              const status = isReg
                ? { cls: 'ok', text: registrationStatus?.checked_in ? 'Вы играете' : 'Вы записаны' }
                : isWait ? { cls: 'wait', text: `Лист ожидания · #${registrationStatus?.position}` }
                : !t.registration_open ? { cls: 'closed', text: 'Регистрация закрыта' }
                : full ? { cls: 'wait', text: 'Мест нет — можно в лист ожидания' }
                : { cls: 'open', text: 'Регистрация открыта' };
              const cta = !isReg && !isWait && t.registration_open ? (full ? 'В лист ожидания' : 'Записаться') : 'Подробнее';
              return (
                <>
                  <span className={`home-tcard__state home-tcard__state--${status.cls}`}>{status.cls === 'ok' && (registrationStatus?.checked_in ? <Gamepad2 size={14} /> : <Check size={14} strokeWidth={3} />)}{status.text}</span>
                  <h3 className="home-tcard__name">{t.name.toString().toUpperCase()}</h3>
                  <div className="home-tcard__pills">
                    {t.start_date && (
                      <span className="home-tcard__pill"><CalendarDays size={13} />{formatDay(t.start_date)}</span>
                    )}
                    {t.start_date && (
                      <span className="home-tcard__pill"><Clock size={13} />{formatTime(t.start_date)}</span>
                    )}
                    {(t.buy_in_price ?? 0) > 0 && (
                      <span className="home-tcard__pill home-tcard__pill--price">{t.buy_in_price!.toLocaleString('ru-RU')} ₽</span>
                    )}
                  </div>
                  {t.max_players > 0 && (
                    <div className="home-tcard__seats">
                      <div className="home-tcard__seats-top">
                        {full
                          ? <span>Все <b>{t.max_players}</b> мест заняты</span>
                          : <span>Осталось <b>{spotsLeft}</b> из {t.max_players} мест</span>}
                      </div>
                      <div className="home-tcard__bar"><i style={{ width: `${fill}%` }} /></div>
                    </div>
                  )}
                  <button
                    type="button"
                    className={`home-tcard__cta ${cta === 'Подробнее' ? 'home-tcard__cta--ghost' : ''}`}
                    onClick={(e) => { e.stopPropagation(); navigate(`/tournament/${t.id}`); }}
                  >
                    {cta}<ChevronRight size={18} />
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      ) : (
        <div
          className="home-tcard home-tcard--empty"
          role="button"
          tabIndex={0}
          onClick={() => navigate('/tournaments')}
        >
          <div className="home-tcard__body">
            <h3 className="home-tcard__name">СКОРО</h3>
            <p className="home-tcard__empty-text">Пока нет запланированных турниров. Следите за обновлениями!</p>
          </div>
        </div>
      )}

      {/* 2.5) Недельная серия визитов */}
      {loyalty && <LoyaltyHomeCard status={loyalty} onOpen={() => navigate('/streak')} />}
      {celebrationVisible && loyalty && (
        <LoyaltyCelebration userId={user.id} status={loyalty} onClose={() => setCelebrate(false)} />
      )}

      {/* 3) Рейтинг - баннер */}
      <div className="home-rating-banner" onClick={() => navigate('/rating')} role="button" tabIndex={0}>
        <div className="home-rating-banner__content">
          <h3 className="home-rating-banner__title">РЕЙТИНГ<br/>HEADS UP</h3>
          <button type="button" className="home-rating-banner__btn" onClick={(e) => { e.stopPropagation(); navigate('/rating'); }}>
            <Star size={15} fill="currentColor" strokeWidth={0} /> Рейтинг игроков
          </button>
        </div>
        <div className="home-rating-banner__info">
          <div className="home-rating-banner__stat">
            <span className="home-rating-banner__statValue">{fmtNum(currentRating)}</span>
            <span className="home-rating-banner__statLabel">Общий рейтинг</span>
          </div>
          <div className="home-rating-banner__stat">
            <span className="home-rating-banner__statValue">#{userPosition?.position ?? '—'}</span>
            <span className="home-rating-banner__statLabel">Общая позиция</span>
          </div>
          <div className="home-rating-banner__stat">
            <span className="home-rating-banner__statValue">{fmtNum(userSeriesPosition?.rating)}</span>
            <span className="home-rating-banner__statLabel">Очки в серии</span>
          </div>
          <div className="home-rating-banner__stat">
            <span className="home-rating-banner__statValue">#{userSeriesPosition?.position ?? '—'}</span>
            <span className="home-rating-banner__statLabel">Место в серии</span>
          </div>
        </div>
      </div>

      {/* 3.5) Пьедестал топ-3 — две вкладки: Общий / Текущая серия. Свайп ←→ переключает. */}
      <PodiumSection
        tab={podiumTab}
        setTab={setPodiumTab}
        seriesLabel={currentSeries.label}
        overallTop={topPlayers}
        seriesTop={seriesTopPlayers}
        navigate={navigate}
      />


      {/* 3.6) Командный зачёт */}
      <TeamHomeCard userId={user.id} navigate={navigate} />

      {/* 4) Реферальная карточка */}
      <ReferralCard userId={user.id} />

      {/* 5) Поддержка + О клубе */}
      <div className="home-grid2">
        <button type="button" className="home-bigcard" onClick={() => navigate('/support')}>
          <h3 className="home-bigcard__title">ПОДДЕРЖКА</h3>
          <div className="home-bigcard__icon"><Phone size={22} /></div>
        </button>

        <button type="button" className="home-bigcard" onClick={() => navigate('/about')}>
          <h3 className="home-bigcard__title">О КЛУБЕ</h3>
          <div className="home-bigcard__icon"><Spade size={22} /></div>
        </button>
      </div>

      {/* 6) Адрес */}
      <div className="home-address-card" onClick={handleOpenMaps} role="button" tabIndex={0}>
        <h3 className="home-address-card__title">АДРЕС</h3>
        <p className="home-address-card__text">{CLUB_ADDRESS}</p>
        <span className="home-address-card__link">Открыть в картах →</span>
      </div>

    </div>
  );
}

/* ─── Team standings card (home) ─── */
function TeamHomeCard({ userId, navigate }: { userId: number; navigate: (p: string) => void }) {
  const [sum, setSum] = useState<{ team: { id: number; name: string; color: string; suit: string; points: number } | null; rank: number | null; total: number } | null>(null);
  useEffect(() => { api.getMyTeamSummary(userId).then(setSum).catch(() => {}); }, [userId]);
  const t = sum?.team;
  return (
    <button type="button" onClick={() => navigate('/teams')} style={{
      width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer', marginBottom: 16,
      background: t ? `linear-gradient(135deg, ${t.color}22, rgba(255,255,255,0.03))` : 'rgba(255,255,255,0.04)',
      borderRadius: 16, padding: 16, borderLeft: `3px solid ${t ? t.color : '#f2c14b'}`,
      display: 'flex', alignItems: 'center', gap: 14,
    }}>
      <div style={{ fontSize: 30, color: t ? t.color : '#f2c14b', display: 'flex' }}>{t ? t.suit : <Trophy size={28} />}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Командный зачёт</div>
        {t
          ? <div style={{ fontWeight: 800, fontSize: 17, color: t.color }}>{t.name} <span style={{ color: '#f2c14b', fontSize: 14, fontWeight: 700 }}>· {t.points.toLocaleString('ru-RU')} б · #{sum?.rank}</span></div>
          : <div style={{ fontWeight: 700, fontSize: 16, color: '#fff' }}>Выбери команду →</div>}
      </div>
      <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 22 }}>›</div>
    </button>
  );
}

/* ─── Podium with two tabs (overall / current series) ─── */
interface PodiumSectionProps {
  tab: PodiumTab;
  setTab: (t: PodiumTab) => void;
  seriesLabel: string;
  overallTop: PlayerRatingRow[];
  seriesTop: PlayerRatingRow[];
  navigate: (path: string) => void;
}

function PodiumSection({ tab, setTab, seriesLabel, overallTop, seriesTop, navigate }: PodiumSectionProps) {
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null || touchStartY.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    // Treat as horizontal swipe only if horizontal motion dominates and exceeds threshold
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) setTab('series');
      else setTab('overall');
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  const players = tab === 'overall' ? overallTop : seriesTop;
  const ratingField = tab === 'overall' ? 'current_rating' : 'monthly_rating';

  return (
    <div className="home-podium-section" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="home-podium-tabs">
        <button
          type="button"
          className={tab === 'overall' ? 'active' : ''}
          onClick={() => setTab('overall')}
        >
          Общий
        </button>
        <button
          type="button"
          className={tab === 'series' ? 'active' : ''}
          onClick={() => setTab('series')}
        >
          {seriesLabel}
        </button>
      </div>

      {players.length >= 3 ? (
        <div className="home-podium">
          {[players[1], players[0], players[2]].map((p, i) => {
            const place = [2, 1, 3][i];
            const charItem = p.equipped_char ? SHOP_ITEMS_MAP.get(p.equipped_char) : null;
            const name = p.display_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Игрок';
            return (
              <div
                key={`${tab}-${p.user_id}`}
                className={`home-podium__player home-podium__place-${place}`}
                onClick={() => navigate(`/profile/${p.user_id}`)}
              >
                <div className="home-podium__avatar-wrap">
                  {place === 1 && <Crown size={20} className="home-podium__crown" />}
                  <div className={`home-podium__avatar home-podium__avatar--${place}`}>
                    {charItem ? (
                      <img src={charItem.image} alt="" />
                    ) : (
                      <span>{name.charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                </div>
                <span className="home-podium__name">{name}</span>
                <span className="home-podium__rating">{fmtNum((p as any)[ratingField])}</span>
                <div className={`home-podium__bar home-podium__bar--${place}`}>
                  {place}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="home-podium-empty">
          {tab === 'series' ? 'В серии пока недостаточно игроков' : 'Нет данных'}
        </div>
      )}
    </div>
  );
}

/* ─── Referral Card ─── */
function ReferralCard({ userId }: { userId: number }) {
  const [copied, setCopied] = useState(false);
  const appUrl = import.meta.env.VITE_APP_URL || 'https://heads-up-poker.ru';
  const referralLink = `${appUrl}/?ref=${userId}`;

  const handleShare = async () => {
    const text = `Присоединяйся к покерному клубу HEADS UP!\n${referralLink}`;

    // Copy to clipboard first
    try { await navigator.clipboard.writeText(referralLink); } catch {}
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);

    // Then offer native share sheet (user picks how to share)
    if (navigator.share) {
      try {
        await navigator.share({ title: 'HEADS UP Poker', text: 'Присоединяйся к покерному клубу HEADS UP!', url: referralLink });
      } catch { /* user cancelled — link already copied */ }
    }
  };

  return (
    <div className="home-referral">
      <div className="home-referral__content">
        <h3 className="home-referral__title">ПРИГЛАСИ ДРУГА</h3>
        <p className="home-referral__desc">Получи карточку бесплатного входа + 50 монет за каждого друга!</p>
      </div>
      <button className="home-referral__btn" onClick={handleShare}>
        {copied ? 'Скопировано!' : 'Поделиться'}
      </button>
    </div>
  );
}
